import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectivityActions } from './store/state/connectivity/connectivity.actions';
import { AuthActions } from './store/state/auth/auth.actions';
import { AppState } from './store/app.state';
import { TranslationService } from './services/translation.service';
import { AuthService } from './services/auth.service';
import { InactivityModalComponent } from './components/modals/inactivity-modal/inactivity-modal.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MatDialogModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(MatDialog);
  private readonly translationService = inject(TranslationService);
  private readonly authService = inject(AuthService);

  constructor() {
    this.translationService.initLanguage();
  }

  @HostListener('window:keydown')
  @HostListener('window:mousemove')
  @HostListener('window:mousedown')
  public checkUserActivity(): void {
    this.store$.dispatch(ConnectivityActions.registerUserActivity());
  }

  @HostListener('document:visibilitychange')
  public refreshSessionOnResume(): void {
    if (document.visibilityState !== 'visible' || !navigator.onLine) {
      return;
    }

    void this.authService.ensureValidSession({
      allowBackgroundRefresh: true,
      isSilentRefresh: true,
    });
  }

  public ngOnInit(): void {
    this.enforceClickjackingProtection();
    this.store$.dispatch(ConnectivityActions.registerUserActivity());
    this.actions$
      .pipe(
        ofType(ConnectivityActions.showInactivityModal),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ secondsLeft }) => this.openInactivityModal(secondsLeft));
  }

  private enforceClickjackingProtection(): void {
    if (window.self === window.top) {
      return;
    }
    console.error('[SECURITY] App loaded in an iframe');

    if (!this.isFramedCrossOrigin()) {
      try {
        window.top!.location.href = window.self.location.href;
        return;
      } catch (e) {
        console.error('[SECURITY] Same-origin iframe breakout failed', e);
      }
    }

    this.store$.dispatch(
      AuthActions.logout({
        msg: 'For your security, this application cannot run inside an embedded frame. Open it in a full browser window.',
      }),
    );
  }

  private isFramedCrossOrigin(): boolean {
    try {
      if (window.self === window.top) {
        return false;
      }
      return window.top!.location.origin !== window.self.location.origin;
    } catch {
      return true;
    }
  }

  private openInactivityModal(secondsLeft: number): void {
    const countdown = secondsLeft > 0 ? secondsLeft : 30;
    this.dialog.open(InactivityModalComponent, {
      id: 'inactivity-dialog',
      width: '475px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data: {
        secondsLeft: countdown,
      },
    });
  }
}
